import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ChatMessage, ChatThread } from '@models';
import {
  ActiveTelehealthCallService,
  CHAT_SERVICE,
  MeService,
} from '@services';
import { merge, of, Subject, switchMap, tap } from 'rxjs';
import { ChatDocumentBubbleComponent } from '../chat-document-bubble/chat-document-bubble.component';
import { DisplayAvatarComponent } from '../display-avatar/display-avatar.component';
import { MicButtonComponent } from '../mic-button/mic-button.component';
import { SpinnerComponent } from '../spinner/spinner.component';

type ThreadItem =
  | { type: 'date'; id: string; label: string }
  | { type: 'message'; id: string; message: ChatMessage };

@Component({
  selector: 'icaredx-telehealth-chat',
  imports: [
    DatePipe,
    MatIconModule,
    ChatDocumentBubbleComponent,
    DisplayAvatarComponent,
    MicButtonComponent,
    SpinnerComponent,
  ],
  templateUrl: './telehealth-chat.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TelehealthChatComponent implements AfterViewChecked {
  private readonly chatService = inject(CHAT_SERVICE);
  private readonly meService = inject(MeService);
  private readonly call = inject(ActiveTelehealthCallService);

  readonly theme = input<'dark' | 'light'>('dark');
  readonly showHeader = input(false);

  protected readonly isLight = computed(() => this.theme() === 'light');
  protected readonly separatorClass = computed(() =>
    this.isLight() ? 'text-gray-400' : 'text-white/50'
  );
  protected readonly nameClass = computed(() =>
    this.isLight() ? 'text-gray-900' : 'text-white'
  );
  protected readonly timeClass = computed(() =>
    this.isLight() ? 'text-gray-400' : 'text-white/40'
  );
  protected readonly emptyTitleClass = computed(() =>
    this.isLight() ? 'text-gray-700' : 'text-white/60'
  );
  protected readonly emptyBodyClass = computed(() =>
    this.isLight() ? 'text-gray-400' : 'text-white/40'
  );
  protected readonly composerBorderClass = computed(() =>
    this.isLight() ? 'border-gray-200' : 'border-white/10'
  );
  protected readonly textareaClass = computed(() =>
    this.isLight()
      ? 'bg-gray-100 text-gray-900 placeholder-gray-400'
      : 'bg-white/5 text-white placeholder-white/40'
  );

  protected readonly currentUserId = this.meService.userId ?? '';

  private readonly threadsLoaded = signal(false);
  protected readonly messagesLoading = signal(true);
  protected readonly sending = signal(false);
  protected readonly pendingMessages = signal<ChatMessage[]>([]);
  protected readonly draft = signal('');

  private readonly textarea =
    viewChild<ElementRef<HTMLTextAreaElement>>('composer');
  private readonly threadEl = viewChild<ElementRef<HTMLElement>>('thread');
  private lastItemCount = 0;
  private stickToBottom = true;
  private initialScrollDone = false;

  private readonly counterpartyId = computed(() => {
    const appointment = this.call.activeAppointment();
    if (!appointment) return undefined;
    return appointment.providerId === this.currentUserId
      ? appointment.patientId
      : appointment.providerId;
  });

  private readonly threads = toSignal(
    this.chatService
      .watchThreads()
      .pipe(tap(() => this.threadsLoaded.set(true))),
    { initialValue: [] as ChatThread[] }
  );

  private readonly thread = computed(() => {
    const id = this.counterpartyId();
    return id
      ? this.threads().find(t => t.counterparty.userId === id)
      : undefined;
  });

  private readonly threadId = computed(() => this.thread()?.threadId);

  private readonly reload$ = new Subject<void>();

  private readonly messages = toSignal(
    merge(
      toObservable(this.threadId).pipe(
        switchMap(id =>
          id ? this.chatService.watchMessages(id) : of([] as ChatMessage[])
        )
      ),
      this.reload$.pipe(
        switchMap(() => {
          const id = this.threadId();
          return id
            ? this.chatService.getMessages(id)
            : of([] as ChatMessage[]);
        }),
        tap(() => this.pendingMessages.set([]))
      )
    ).pipe(tap(() => this.messagesLoading.set(false))),
    { initialValue: [] as ChatMessage[] }
  );

  private readonly allMessages = computed(() => [
    ...this.messages(),
    ...this.pendingMessages(),
  ]);

  protected readonly loading = computed(
    () =>
      !this.threadsLoaded() ||
      (this.threadId() !== undefined && this.messagesLoading())
  );

  protected readonly canSend = computed(() => this.threadId() !== undefined);

  protected readonly threadItems = computed<ThreadItem[]>(() => {
    const items: ThreadItem[] = [];
    let currentDateKey = '';
    for (const message of this.allMessages()) {
      const date = new Date(message.sentAt);
      const dateKey = date.toDateString();
      if (dateKey !== currentDateKey) {
        currentDateKey = dateKey;
        items.push({
          type: 'date',
          id: `date-${dateKey}`,
          label: this.formatDateLabel(date),
        });
      }
      items.push({ type: 'message', id: message.id, message });
    }
    return items;
  });

  constructor() {
    effect(() => {
      const id = this.threadId();
      if (id) {
        this.chatService.markThreadAsRead(id).subscribe();
      }
    });
  }

  protected isOwn(message: ChatMessage): boolean {
    return message.senderId === this.currentUserId;
  }

  protected senderName(message: ChatMessage): string {
    if (this.isOwn(message)) return 'You';
    const cp = this.thread()?.counterparty;
    return cp ? `${cp.givenName} ${cp.familyName}` : message.senderName;
  }

  protected avatarName(message: ChatMessage): {
    givenName: string;
    familyName: string;
  } {
    if (this.isOwn(message)) {
      const me = this.meService.me();
      return {
        givenName: me?.givenName ?? '',
        familyName: me?.familyName ?? ' ',
      };
    }
    const cp = this.thread()?.counterparty;
    return {
      givenName: cp?.givenName ?? message.senderName,
      familyName: cp?.familyName ?? ' ',
    };
  }

  protected avatarUrl(message: ChatMessage): string {
    if (this.isOwn(message)) return this.meService.me()?.avatarUrl ?? '';
    return this.thread()?.counterparty.avatarUrl ?? '';
  }

  protected bubbleClasses(message: ChatMessage): string {
    const base =
      'max-w-[80%] text-sm leading-relaxed px-4 py-3 whitespace-pre-wrap break-words';
    if (this.isOwn(message)) {
      return `${base} bg-primary-600 text-white rounded-tl-[18px] rounded-bl-[18px] rounded-br-[18px]`;
    }
    const incoming = this.isLight()
      ? 'bg-gray-100 text-gray-800'
      : 'bg-white/10 text-white';
    return `${base} ${incoming} rounded-tr-[18px] rounded-br-[18px] rounded-bl-[18px]`;
  }

  protected onInput(event: Event): void {
    this.draft.set((event.target as HTMLTextAreaElement).value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  protected onTranscribed(transcript: string): void {
    this.draft.update(text => (text ? `${text} ${transcript}` : transcript));
  }

  protected send(): void {
    const text = this.draft().trim();
    const threadId = this.threadId();
    if (!text || this.sending() || !threadId) return;

    const me = this.meService.me();
    const optimisticId = `optimistic-${crypto.randomUUID()}`;
    const optimistic: ChatMessage = {
      id: optimisticId,
      senderId: this.currentUserId,
      senderName: me ? `${me.givenName} ${me.familyName}` : '',
      text,
      sentAt: new Date(),
    };
    this.pendingMessages.update(msgs => [...msgs, optimistic]);
    this.sending.set(true);
    this.draft.set('');
    this.textarea()?.nativeElement.focus();

    this.chatService.sendMessage(threadId, text).subscribe({
      next: () => {
        this.reload$.next();
        this.sending.set(false);
      },
      error: () => {
        this.pendingMessages.update(msgs =>
          msgs.map(m => (m.id === optimisticId ? { ...m, failed: true } : m))
        );
        this.sending.set(false);
      },
    });
  }

  protected retry(message: ChatMessage): void {
    this.pendingMessages.update(msgs => msgs.filter(m => m.id !== message.id));
    this.draft.set(message.text);
    this.send();
  }

  protected onScroll(): void {
    const el = this.threadEl()?.nativeElement;
    if (!el) return;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  ngAfterViewChecked(): void {
    const el = this.threadEl()?.nativeElement;
    if (!el) return;

    const count = this.threadItems().length;

    if (!this.initialScrollDone) {
      if (count === 0) return;
      el.scrollTop = el.scrollHeight;
      this.lastItemCount = count;
      this.initialScrollDone = true;
      return;
    }

    if (count === this.lastItemCount) return;
    this.lastItemCount = count;
    if (!this.stickToBottom) return;
    el.scrollTop = el.scrollHeight;
  }

  private formatDateLabel(date: Date): string {
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return 'Today';

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
}
